import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Check, AlertCircle, ShieldCheck, Loader2, ChevronRight, ChevronLeft, Send, Wand2,
  FileText, Upload, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo } from '../components';
import { Button } from '../components';
import { buildCandidateSummary, formatSummaryDate } from '../utils/candidateSummary';
import { inputCls, labelCls, cardCls } from './publicFormStyles';

/**
 * Personal details form — PUBLIC page at /onboarding/:token
 *
 * Step 3 of recruitment: the SECOND thing a candidate is sent, after an
 * interview has gone well. Instead of the manager typing a new starter's whole
 * record into the Add Staff form, the candidate fills it in themselves.
 *
 * The split between this and the pre-interview form (/pre-interview/:token) is
 * the point of having two forms at all. That one asks what an interviewer needs
 * in front of them. This one asks the things that only matter once somebody has
 * the job — where they live, who to call in an emergency, and where their wages
 * go — and nobody should have to hand those over to be considered.
 *
 * Two consequences worth knowing before editing:
 *   · NOTHING uploads here. Every document in recruitment is asked for on the
 *     pre-interview form, so there is one place files live and one place to
 *     look for them.
 *   · Employment history is not asked here either — it moved to step 1, where
 *     it is read before the interview rather than filed after it.
 *
 * No auth header is sent — the token in the URL IS the credential, so this uses
 * plain fetch rather than the authenticated axios instance.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');

/** Stored photos come back as /api/files/... paths. */
const photoHref = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_ORIGIN}${url}`);

/**
 * The form is walked through in steps so a long list of fields doesn't read as
 * a wall of inputs. Required fields are validated once, at the review step.
 *
 * There is no compliance step: Right to Work and DBS were sent and checked
 * before the interview, and asking somebody to re-type the numbers off
 * documents we already hold is work for them and a worse copy for us. There is
 * no employment step either — the job title and contract are the employer's
 * side of the deal, not a detail about the candidate.
 */
const BASE_STEPS = [
  { id: 'personal', title: 'About you', blurb: 'Your legal name and personal details.' },
  { id: 'address', title: 'Your address', blurb: 'Where you live now, and where you lived before.' },
  { id: 'emergency', title: 'Emergency contact', blurb: 'Who we should call if something happens at work.' },
  { id: 'bank', title: 'Bank details', blurb: 'So your first wages reach the right account.' },
  { id: 'referees', title: 'Referee details', blurb: 'Tell us who can provide a reference for you.' },
];

const REVIEW_STEP = { id: 'review', title: 'Review & submit', blurb: 'Check everything over before you send it.' };

/**
 * The documents step only exists for a candidate a manager asked something
 * extra of. Most people never see it, which is the point — nobody is shown an
 * empty "Documents" step to click past.
 */
const stepsFor = (extraDocuments) => (extraDocuments?.length
  ? [...BASE_STEPS, { id: 'documents', title: 'Documents', blurb: 'The extra documents your manager asked for.' }, REVIEW_STEP]
  : [...BASE_STEPS, REVIEW_STEP]);

// Only what the candidate legitimately owns. Mirrors
// CANDIDATE_EDITABLE_DETAIL_FIELDS in api/utils/recruitment.js — the backend
// enforces the same allowlist, so anything extra here would simply be ignored.
//
// These are the Basic details of the Add Staff form, field for field, so a
// candidate who fills this in themselves produces the same record a manager
// would have typed. Changing one form means changing the other.
const EMPTY_ANSWERS = {
  // About you
  preferred_name: '', dob: '', ni_number: '', nationality: '', gender: '', ethnicity: '',
  disability_status: '', photo_url: '',
  // Address
  address_line1: '', address_line2: '', address_town: '', address_postcode: '',
  address_country: 'United Kingdom', address_under_3_years: false,
  current_address: '', previous_addresses: '',
  // Emergency contact, and next of kin. Same shape as the Add Staff form:
  // `next_of_kin` is an OBJECT, and the tickbox means "copy the emergency
  // contact into it on save" so nobody types the same person twice.
  emergency_contact_name: '', emergency_contact_rel: '', emergency_contact_phone: '',
  next_of_kin: { name: '', relationship: '', phone: '' },
  // UI only — stripped before the answers are sent.
  kin_same_as_emergency: true,
  // Payroll
  bank_account_name: '', bank_sort_code: '', bank_account_number: '',
  bank_building_society_ref: '',
};

// Two groups are deliberately absent, and both are enforced by the API's own
// allowlist so adding them back here would silently do nothing:
//
//   Employment terms (job title, contract, notice period) — the employer's side
//   of the deal, set by the manager, with the start date agreed at step 4.
//
//   Compliance reference numbers (rtw_doc_type, dbs_cert_number and the rest) —
//   those documents were sent and read before the interview.

/**
 * The bank fields are the one place on this form where a typo costs real money.
 *
 * Both are restricted as they are typed — digits only, capped at their length,
 * and the sort code hyphenated into 04-00-04 — rather than accepted and
 * complained about later. Typing is where the mistake happens, so that is where
 * it should become impossible.
 *
 * The same rules run again on the server (bankDetailProblems in
 * api/utils/recruitment.js): this form is public, so the browser is a
 * convenience and never the check.
 */
const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

const BANK_RULES = {
  bank_sort_code: {
    digits: 6,
    format: (value) => digitsOnly(value).slice(0, 6).replace(/(\d{2})(?=\d)/g, '$1-'),
    problem: 'A sort code is 6 digits, like 04-00-04.',
  },
  bank_account_number: {
    digits: 8,
    format: (value) => digitsOnly(value).slice(0, 8),
    problem: 'An account number is 8 digits.',
  },
};

/** Which bank fields are filled in but the wrong length. */
const bankProblems = (answers) => Object.entries(BANK_RULES)
  .filter(([field, rule]) => {
    const typed = digitsOnly(answers[field]);
    return typed.length > 0 && typed.length !== rule.digits;
  })
  .map(([, rule]) => rule.problem);

// Answers that are not free text, so the draft loader leaves them alone.
const BOOLEAN_ANSWERS = ['address_under_3_years', 'kin_same_as_emergency'];

// Answers that are OBJECTS. toText() would flatten one into "name: …" lines and
// the three inputs behind it would come back empty.
const OBJECT_ANSWERS = ['next_of_kin'];

/**
 * Answers the draft loader must hand back as an ARRAY rather than text.
 *
 * Empty, and that is correct: employment history and the preferred working days
 * both left this form, and `previous_addresses` is edited as a textarea — it is
 * stored as an array but toText() joins it back into lines on the way in, which
 * is exactly what the textarea wants. Listing it here would put an array into a
 * text box.
 */
const LIST_ANSWERS = [];

// Required before submitting. Kept in step with PERSONAL_DETAILS_REQUIRED_FIELDS
// in api/utils/recruitment.js, which is what the submit endpoint checks and what
// decides whether the manager's step 3 tiles read "Complete" — so a candidate is
// never told "looks fine" here and then rejected.
const REQUIRED = {
  dob: 'Date of birth',
  current_address: 'Home address',
  emergency_contact_name: 'Emergency contact name',
  emergency_contact_phone: 'Emergency contact phone',
  bank_account_name: 'Name on the bank account',
  bank_sort_code: 'Sort code',
  bank_account_number: 'Account number',
};

// The same option lists the Add Staff form offers, so the two forms cannot drift
// into storing different words for the same thing.
const GENDERS = ['Prefer not to say', 'Female', 'Male', 'Non-binary', 'Self-describe'];

/**
 * Sample answers behind the "Fill test data" button, mirroring the Add Staff
 * form's dummy-data helper so the whole flow can be exercised without typing
 * every field. Dates are computed relative to today so they never go stale.
 *
 * @param {number} seed  varies the identifying values between clicks
 */
const testAnswers = (seed) => {
  const today = new Date();
  const iso = (date) => date.toISOString().slice(0, 10);
  const shift = (years, months = 0) => iso(
    new Date(today.getFullYear() + years, today.getMonth() + months, today.getDate()),
  );

  return {
    preferred_name: 'Alex',
    dob: shift(-32),
    ni_number: `QQ ${seed} 34 56 C`,
    nationality: 'British',
    gender: 'Female',
    ethnicity: 'White British',
    disability_status: 'None declared',
    address_line1: `${seed} Meadow Lane`,
    address_line2: '',
    address_town: 'Manchester',
    address_postcode: 'M14 7AB',
    address_country: 'United Kingdom',
    address_under_3_years: true,
    previous_addresses: '4 Birch Grove, Manchester, M20 3EA — Mar 2019 to Jan 2023',
    emergency_contact_name: 'Jordan Blake',
    emergency_contact_rel: 'Partner',
    emergency_contact_phone: `+44 7700 9${seed}18`,
    // Left as the emergency contact — the tickbox default copies it across.
    kin_same_as_emergency: true,
    bank_account_name: 'A Taylor',
    bank_sort_code: '04-00-04',
    bank_account_number: `1234${seed}`,
  };
};

/** A JSON column may come back as an array/object; show it as editable text. */
const toText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join('\n');
  if (typeof value === 'object') return Object.entries(value).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n');
  return String(value);
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F5F9FF] dark:bg-[#1b1f26] px-4 py-10">
    <Logo size="md" className="justify-center mb-8" />
    {children}
  </div>
);

const Centered = ({ icon, tone, title, children }) => (
  <Shell>
    <div className="bg-white dark:bg-[#262b33] rounded-[24px] shadow-xl p-6 sm:p-8 max-w-[480px] w-full mx-auto text-center border border-gray-100 dark:border-gray-800">
      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${tone}`}>{icon}</div>
      <h2 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">{title}</h2>
      <div className="text-[#64748B] dark:text-gray-400 text-[15px] leading-relaxed">{children}</div>
    </div>
  </Shell>
);

const CandidateOnboardingForm = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  // "Fill test data" is a development aid, and this page is public — a real
  // candidate opening their emailed link must never see it. Shown on the dev
  // server, or anywhere with ?test=1 so a deployed build can still be exercised.
  const showTestButton = import.meta.env.DEV || searchParams.get('test') === '1';

  /**
   * ?step=documents — the deep link in a "one more document" email.
   *
   * Somebody who has already filled this form in and been asked for one extra
   * thing should not have to walk back through four steps to find where it
   * goes. Applied once, after the form loads and the step list is known, and
   * only if that step actually exists for them.
   */
  const requestedStep = searchParams.get('step');
  const jumpedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  // The name is entered in three parts, as it is on the Add Staff form, but
  // users.name stores one string. Composed on save, split back on load.
  const [contact, setContact] = useState({ first_name: '', middle_name: '', last_name: '', phone: '' });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  /**
   * Typed twice, on purpose. An account number is eight digits with no
   * checksum: 12345687 is every bit as valid as 12345678, so nothing on either
   * side of this form can tell a transposition from a correct answer. Asking
   * for it again is the only check that exists.
   *
   * Deliberately NOT part of `answers` — it is a check, not a fact about the
   * candidate, and there is no column for it. It is seeded from the saved value
   * when a draft is reopened, so somebody coming back is not made to retype a
   * number they already confirmed.
   */
  const [confirmAccount, setConfirmAccount] = useState('');

  /**
   * The referees the candidate names.
   *
   * Kept out of `answers` because the API treats them separately: the candidate
   * owns only the contact half of each row, while the request state and the
   * referee's own answers are written by other people entirely.
   */
  const [referees, setReferees] = useState([]);
  const blankReferee = (type = 'employment') => ({
    id: `new-${Math.random().toString(36).slice(2, 9)}`,
    type,
    name: '',
    email: '',
    relationship: '',
    permission: false,
    status: 'Not requested',
    received: false,
  });
  const [uploadingId, setUploadingId] = useState(null);

  // One hidden file input, retargeted per requested-document row.
  const fileInputRef = useRef(null);
  const pendingDocRef = useRef(null);

  /**
   * Take a server payload into the form.
   *
   * `keepTyped` leaves the ANSWER BOXES ALONE and refreshes only what the
   * server owns — the document list, the referee ids, the link state.
   *
   * It exists because of a real bug. Uploading the profile photo, which sits at
   * the BOTTOM of the first section, returned the candidate's stored record and
   * this function wrote all of it back over the form — so every box they had
   * just filled in above the photo reverted to blank, and they had to type the
   * whole section again. The upload's job is the file; it has no business
   * deciding what is in the name field.
   *
   * The referee rows are typed in too, so they are left alone as well. They
   * still have to be re-read after a SAVE — a new row carries a local
   * "new-xxx" id and the server swaps it for a real one, and ignoring that
   * would create a second copy of the same referee on the next save (see
   * mergeCandidateReferences) — but a save is not a keepTyped call.
   */
  const applyPayload = useCallback((payload, { keepTyped = false } = {}) => {
    setData(payload);

    if (payload.locked) setSubmitted(true);

    // Only on the first load — a later save must not yank the candidate back
    // to the documents step while they are typing somewhere else.
    if (!jumpedRef.current && requestedStep) {
      const index = stepsFor(payload.extra_documents).findIndex((s) => s.id === requestedStep);
      if (index >= 0) setStep(index);
      jumpedRef.current = true;
    }

    if (keepTyped) return;

    setConfirmAccount(payload?.answers?.bank_account_number || '');
    // Start everybody with the two the form asks for, so the minimum is shown
    // rather than explained and then discovered on submit.
    const savedReferees = Array.isArray(payload.references) ? payload.references : [];
    setReferees(savedReferees.length
      ? savedReferees
      : [blankReferee('employment'), blankReferee('character')]);
    setAnswers({
      ...EMPTY_ANSWERS,
      ...Object.fromEntries(
        Object.keys(EMPTY_ANSWERS).map((key) => {
          const saved = payload.answers?.[key];

          if (key === 'kin_same_as_emergency') {
            // Not stored. Derived: a record with no next of kin of its own is
            // one where the tickbox was on, which is also the right default for
            // somebody opening the form for the first time.
            const kin = payload.answers?.next_of_kin;
            const hasOwnKin = typeof kin === 'string' ? Boolean(kin.trim()) : Boolean(kin?.name);
            return [key, !hasOwnKin];
          }
          if (OBJECT_ANSWERS.includes(key)) {
            // A record saved before this was an object holds a bare string.
            return [key, typeof saved === 'object' && saved !== null
              ? { name: '', relationship: '', phone: '', ...saved }
              : { name: saved || '', relationship: '', phone: '' }];
          }
          if (BOOLEAN_ANSWERS.includes(key)) return [key, !!saved];
          // Lists stay lists. toText() below would flatten an employment
          // history into a string and lose every entry.
          if (LIST_ANSWERS.includes(key)) return [key, Array.isArray(saved) ? saved : []];
          if (key.endsWith('_date') || key === 'dob' || key.endsWith('_expiry')) {
            return [key, String(saved || '').slice(0, 10)];
          }
          // An untouched draft has no value for a field that ships with a
          // default (country, employment type); keep the default rather than
          // blanking it.
          const text = toText(saved);
          return [key, text || EMPTY_ANSWERS[key]];
        }),
      ),
    });

    // First word is the forename, last is the surname, anything between is the
    // middle name — the same split the Add Staff form uses.
    const parts = String(payload.candidate?.name || '').trim().split(/\s+/).filter(Boolean);
    setContact({
      first_name: parts[0] || '',
      middle_name: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
      last_name: parts.length > 1 ? parts[parts.length - 1] : '',
      phone: payload.candidate?.phone || '',
    });
  }, [requestedStep]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/recruitment/form/${token}`);
      const result = await response.json();
      if (result.success) applyPayload(result.data);
      else setError(result.message || 'This link is not valid.');
    } catch {
      setError('We could not reach the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, applyPayload]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  // users.name is one string built from the three name inputs; everything
  // downstream (the directory, emails, the manager's review) reads it.
  const fullName = [contact.first_name, contact.middle_name, contact.last_name]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');

  // The address is entered in parts but current_address is what the staff
  // profile and the manager's review read, so it is rebuilt on every save —
  // exactly as the Add Staff form does it.
  const composedAddress = [
    answers.address_line1, answers.address_line2, answers.address_town,
    answers.address_postcode, answers.address_country,
  ].map((part) => String(part || '').trim()).filter(Boolean).join(', ');

  // Address history is only kept while the "less than 3 years" tick is on,
  // so unticking it cannot leave an orphaned previous address behind.
  const previousAddressesToSave = answers.address_under_3_years ? answers.previous_addresses : '';

  /**
   * A next of kin who IS the emergency contact is copied across rather than
   * typed twice — exactly what the tickbox says, and what the Add Staff form
   * does on save. A kin with nothing in it is stored as null rather than three
   * empty strings, so "no next of kin given" reads as absent, not as blank.
   */
  const nextOfKinToSave = (() => {
    const kin = answers.kin_same_as_emergency
      ? {
        name: answers.emergency_contact_name || '',
        relationship: answers.emergency_contact_rel || '',
        phone: answers.emergency_contact_phone || '',
      }
      : (answers.next_of_kin || { name: '', relationship: '', phone: '' });
    return (kin.name || kin.relationship || kin.phone) ? kin : null;
  })();

  /**
   * Contract end date only applies to a fixed-term contract. Moving away from
   * Fixed-term clears it, so a stale end date cannot be submitted against a
   * permanent contract.
   */
  /**
   * Fill every field with plausible sample data, the same way the Add Staff
   * form's "Fill Test Data" button does.
   */
  const fillTestData = () => {
    const seed = Math.floor(10 + Math.random() * 89);
    setAnswers((prev) => ({ ...prev, ...testAnswers(seed) }));
    setContact({ first_name: 'Alex', middle_name: '', last_name: `Taylor ${seed}`, phone: `+44 7700 9${seed}42` });
    toast.success('Test data filled.');
  };

  /** Save a draft (submit=false) or submit the form (submit=true). */
  const persist = async (submit) => {
    const setter = submit ? setSubmitting : setSaving;
    try {
      setter(true);
      const response = await fetch(`${API_URL}/recruitment/form/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          references: referees.filter((row) => row.name || row.email),
          answers: {
            ...answers,
            current_address: composedAddress,
            previous_addresses: previousAddressesToSave,
            next_of_kin: nextOfKinToSave,
            // UI only — the API's allowlist would drop it anyway, but sending
            // it would imply it means something on the record.
            kin_same_as_emergency: undefined,
          },
          name: fullName,
          phone: contact.phone,
          submit,
        }),
      });
      const result = await response.json();

      if (!result.success) {
        // The submit path returns exactly what's still missing — show that
        // rather than a generic failure.
        const detail = [...(result.missing_fields || []), ...(result.missing_documents || [])];
        toast.error(detail.length ? `Still needed: ${detail.join(', ')}` : (result.message || 'Could not save.'));
        return false;
      }

      applyPayload(result.data);
      if (submit) setSubmitted(true);
      else toast.success(result.message);
      return true;
    } catch {
      toast.error('We could not save your answers. Please try again.');
      return false;
    } finally {
      setter(false);
    }
  };

  const pickFile = (storageId) => {
    pendingDocRef.current = storageId;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  /** The photo shares the one hidden input, under a reserved id. */
  const pickPhoto = () => pickFile('photo');

  /**
   * Send the file for one requested document.
   *
   * The id is a slot the manager opened; the endpoint refuses anything else, so
   * this can only ever fill a request rather than create one.
   */
  const handleFileChosen = async (event) => {
    const file = event.target.files?.[0];
    const storageId = pendingDocRef.current;
    if (!file || !storageId) return;

    try {
      setUploadingId(storageId);
      const body = new FormData();
      body.append('file', file);
      // The photo is one column rather than a slot in the requested-document
      // list, so it has its own endpoint.
      const url = storageId === 'photo'
        ? `${API_URL}/recruitment/form/${token}/photo`
        : `${API_URL}/recruitment/form/${token}/documents/${storageId}`;
      const response = await fetch(url, { method: 'POST', body });
      const result = await response.json();
      if (result.success) {
        // Nothing the candidate has typed is touched — see applyPayload. The
        // photo is the one answer this request actually produced, so it is the
        // one answer it may write.
        applyPayload(result.data, { keepTyped: true });
        if (storageId === 'photo') set('photo_url', result.data?.answers?.photo_url || '');
        toast.success(result.message);
      } else {
        toast.error(result.message || 'Upload failed.');
      }
    } catch {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploadingId(null);
      pendingDocRef.current = null;
    }
  };

  const removeUpload = async (storageId) => {
    try {
      setUploadingId(storageId);
      const response = await fetch(`${API_URL}/recruitment/form/${token}/documents/${storageId}`, { method: 'DELETE' });
      const result = await response.json();
      if (result.success) applyPayload(result.data);
      else toast.error(result.message);
    } catch {
      toast.error('Could not remove that file. Please try again.');
    } finally {
      setUploadingId(null);
    }
  };

  // Save quietly when moving between steps so nothing is lost on a closed tab.
  const goNext = async () => {
    if (step < stepsFor(data?.extra_documents).length - 1) {
      await persist(false);
      setStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setStep((prev) => Math.max(0, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Centered icon={<Loader2 className="w-8 h-8 text-blue-600 animate-spin" />} tone="bg-blue-50 dark:bg-blue-900/20" title="Loading your form">
        One moment.
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered icon={<AlertCircle className="w-8 h-8 text-rose-600" />} tone="bg-rose-50 dark:bg-rose-900/20" title="This link isn't available">
        {error}
      </Centered>
    );
  }

  // Already submitted — the link stays useful as a receipt: everything they
  // sent, read-only, so re-opening it answers "what did I actually put?" instead
  // of showing a bare thank-you.
  if (submitted) {
    const summary = buildCandidateSummary({
      name: fullName || data?.candidate?.name,
      email: data?.candidate?.email,
      phone: contact.phone || data?.candidate?.phone,
      details: answers,
    });

    return (
      <Shell>
        <div className="max-w-[860px] mx-auto space-y-5">
          <div className={cardCls}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl flex items-center justify-center shrink-0">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-[20px] font-bold text-[#1E293B] dark:text-gray-100">
                  Thank you — that's everything we need
                </h2>
                <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1">
                  Your information was sent to {data?.candidate?.organisation || 'the recruiting team'}
                  {data?.submitted_at ? ` on ${formatSummaryDate(data.submitted_at)}` : ''} for review. They'll be in touch with
                  the next steps — you don't need to do anything else.
                </p>
                <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500 mt-2">
                  A copy of what you sent is below. If something needs correcting, contact the recruiting manager and they can
                  reopen the form for you.
                </p>
              </div>
            </div>
          </div>

          {summary.map((group) => (
            <div key={group.title} className={cardCls}>
              <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">{group.title}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {group.fields.map((field) => (
                  <div key={field.label} className={field.full ? 'sm:col-span-2' : ''}>
                    <div className="text-[12px] font-semibold text-[#94A3B8] dark:text-gray-500 uppercase tracking-wide">
                      {field.label}
                    </div>
                    <div className="text-[14px] text-[#1E293B] dark:text-gray-200 mt-1 whitespace-pre-wrap break-words">
                      {field.value || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

        </div>
      </Shell>
    );
  }

  // current_address is composed from the address parts rather than typed, so it
  // is checked against the composed value the save will actually send.
  const answersForCheck = { ...answers, current_address: composedAddress };
  const missing = Object.entries(REQUIRED).filter(([key]) => !answersForCheck[key]).map(([, label]) => label);
  // A document a manager asked for by name is not optional — the backend's
  // submit check agrees, so the candidate is never told "looks fine" and then
  // rejected.
  const missingDocs = (data.extra_documents || []).filter((row) => !row.received).map((row) => row.label);
  // Wrong is separate from missing: a five-digit sort code IS filled in, so the
  // required-fields check is happy with it and would let it through.
  const badBank = bankProblems(answers);
  // Only a mismatch between two things they have actually typed. An empty
  // confirm box while the account number is still being entered is not an
  // error yet, but it does keep Submit disabled below.
  const accountMismatch = Boolean(answers.bank_account_number)
    && Boolean(confirmAccount)
    && answers.bank_account_number !== confirmAccount;
  const accountUnconfirmed = Boolean(answers.bank_account_number) && !confirmAccount;

  const canSubmit = missing.length === 0
    && missingDocs.length === 0
    && badBank.length === 0
    && !accountMismatch
    && !accountUnconfirmed;

  /**
   * The hint under a bank field: grey while it is being typed, red once what is
   * there is definitely the wrong length. Never red on an empty box — that is a
   * field they have not reached yet, not a mistake they have made.
   */
  const bankHint = (field) => {
    const typed = digitsOnly(answers[field]);
    const wrong = typed.length > 0 && typed.length !== BANK_RULES[field].digits;
    return `text-[12px] mt-1.5 ${wrong
      ? 'text-rose-600 dark:text-rose-400 font-semibold'
      : 'text-[#94A3B8] dark:text-gray-500'}`;
  };

  const extraDocuments = data.extra_documents || [];
  const STEPS = stepsFor(extraDocuments);
  // A manager can withdraw a request while the candidate has the form open, so
  // the step list can shrink underneath them. Clamp rather than render nothing.
  const current = STEPS[Math.min(step, STEPS.length - 1)];

  return (
    <Shell>
      <input
        ref={fileInputRef} type="file" className="hidden"
        onChange={handleFileChosen} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
      />

      <div className="max-w-[860px] mx-auto space-y-5">
        {/* Intro */}
        <div className={cardCls}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[20px] font-bold text-[#1E293B] dark:text-gray-100">
                Welcome{data.candidate?.name ? `, ${data.candidate.name.split(' ')[0]}` : ''}
              </h2>
              <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1">
                Please complete your details for your {data.candidate?.role_applied_for || 'role'} application
                {data.candidate?.organisation ? ` with ${data.candidate.organisation}` : ''}. You can save and come back to this
                link at any time before submitting.
              </p>
              <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500 mt-2">
                We will never ask you for a password or PIN on this form.
              </p>
            </div>
            {showTestButton && (
              <Button
                variant="secondary" size="sm" leftIcon={<Wand2 className="w-4 h-4" />}
                className="shrink-0" onClick={fillTestData}
              >
                Fill test data
              </Button>
            )}
          </div>
        </div>

        {/* Step progress */}
        <div className={`${cardCls} !py-5`}>
          <div className="flex items-center gap-2 mb-3">
            {STEPS.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${index <= step ? 'bg-blue-600' : 'bg-[#E2E8F0] dark:bg-[#31373f]'}`}
              />
            ))}
          </div>
          {/* "Step 3 of 6" goes above the title on a phone rather than fighting
              a wrapping blurb for the same line. */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-3">
            <div className="min-w-0">
              <div className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">{current.title}</div>
              <div className="text-[13px] text-[#64748B] dark:text-gray-400 mt-0.5">{current.blurb}</div>
            </div>
            <div className="text-[12.5px] font-medium text-[#94A3B8] dark:text-gray-500 shrink-0 tabular-nums">
              Step {step + 1} of {STEPS.length}
            </div>
          </div>
        </div>

        <div className={cardCls}>
          {/* ---------- About you ----------
              Name & account plus Personal & identity from the Add Staff form's
              Basic details. No user ID or PIN: those are generated when the
              manager converts the candidate into a staff member. */}
          {current.id === 'personal' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
              <div>
                <label className={labelCls}>First name <span className="text-rose-500">*</span></label>
                <input className={inputCls} value={contact.first_name} onChange={(e) => setContact((p) => ({ ...p, first_name: e.target.value }))} placeholder="First name" />
              </div>
              <div>
                <label className={labelCls}>Middle name</label>
                <input className={inputCls} value={contact.middle_name} onChange={(e) => setContact((p) => ({ ...p, middle_name: e.target.value }))} placeholder="Optional" />
              </div>
              <div>
                <label className={labelCls}>Surname <span className="text-rose-500">*</span></label>
                <input className={inputCls} value={contact.last_name} onChange={(e) => setContact((p) => ({ ...p, last_name: e.target.value }))} placeholder="Surname" />
              </div>
              <div>
                <label className={labelCls}>Preferred name</label>
                <input className={inputCls} value={answers.preferred_name} onChange={(e) => set('preferred_name', e.target.value)} placeholder="Name used at work" />
              </div>
              <div>
                <label className={labelCls}>Email address</label>
                <input className={`${inputCls} bg-[#F8FAFC] dark:bg-[#31373f] text-[#94A3B8]`} value={data.candidate?.email || ''} readOnly />
              </div>
              <div>
                <label className={labelCls}>Telephone</label>
                <input className={inputCls} value={contact.phone} onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))} placeholder="e.g. +44 7700 900000" />
              </div>

              <div className="md:col-span-3 pt-2 border-t border-[#F1F5F9] dark:border-gray-800" />

              <div>
                <label className={labelCls}>Date of birth <span className="text-rose-500">*</span></label>
                <input type="date" className={inputCls} value={answers.dob} onChange={(e) => set('dob', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>National Insurance number <span className="text-rose-500">*</span></label>
                <input className={inputCls} value={answers.ni_number} onChange={(e) => set('ni_number', e.target.value)} placeholder="e.g. QQ 12 34 56 C" />
              </div>
              <div>
                <label className={labelCls}>Nationality</label>
                <input className={inputCls} value={answers.nationality} onChange={(e) => set('nationality', e.target.value)} placeholder="Nationality" />
              </div>
              <div>
                <label className={labelCls}>Gender <span className="text-[#94A3B8] font-normal">(optional)</span></label>
                <select className={inputCls} value={answers.gender} onChange={(e) => set('gender', e.target.value)}>
                  {GENDERS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Ethnicity <span className="text-[#94A3B8] font-normal">(optional)</span></label>
                <input className={inputCls} value={answers.ethnicity} onChange={(e) => set('ethnicity', e.target.value)} placeholder="Optional" />
              </div>
              <div className="md:col-span-3">
                <label className={labelCls}>Disability or reasonable adjustments <span className="text-[#94A3B8] font-normal">(only what we need to support you at work)</span></label>
                <textarea rows="3" className={`${inputCls} resize-none`} value={answers.disability_status} onChange={(e) => set('disability_status', e.target.value)} placeholder="Optional" />
              </div>

              {/* A photo, so somebody who applied through recruitment does not
                  arrive in the staff directory without a face. Optional, and
                  saved on its own the moment it is chosen — it is one column,
                  not part of the draft. */}
              <div className="md:col-span-3 pt-5 mt-1 border-t border-[#E2E8F0] dark:border-gray-800">
                <label className={labelCls}>Profile photo <span className="text-[#94A3B8] font-normal">(optional)</span></label>
                <div className="flex flex-wrap items-center gap-4">
                  {answers.photo_url ? (
                    <img
                      src={photoHref(answers.photo_url)}
                      alt=""
                      className="w-16 h-16 rounded-full object-cover border border-[#E2E8F0] dark:border-gray-700"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-[#F1F5F9] dark:bg-[#31373f] flex items-center justify-center text-[#94A3B8]">
                      <Upload className="w-5 h-5" />
                    </div>
                  )}
                  <Button
                    variant="secondary" size="sm" leftIcon={<Upload className="w-4 h-4" />}
                    loading={uploadingId === 'photo'} onClick={pickPhoto}
                  >
                    {answers.photo_url ? 'Replace photo' : 'Add a photo'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ---------- Address ---------- */}
          {current.id === 'address' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div className="md:col-span-2">
                  <label className={labelCls}>Address line 1</label>
                  <input className={inputCls} value={answers.address_line1} onChange={(e) => set('address_line1', e.target.value)} placeholder="House number and street" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Address line 2 <span className="text-[#94A3B8] font-normal">(optional)</span></label>
                  <input className={inputCls} value={answers.address_line2} onChange={(e) => set('address_line2', e.target.value)} placeholder="Flat, building or area" />
                </div>
                <div>
                  <label className={labelCls}>Town or city</label>
                  <input className={inputCls} value={answers.address_town} onChange={(e) => set('address_town', e.target.value)} placeholder="Town or city" />
                </div>
                <div>
                  <label className={labelCls}>Postcode</label>
                  <input className={inputCls} value={answers.address_postcode} onChange={(e) => set('address_postcode', e.target.value)} placeholder="e.g. SW1A 1AA" />
                </div>
                <div>
                  <label className={labelCls}>Country</label>
                  <input className={inputCls} value={answers.address_country} onChange={(e) => set('address_country', e.target.value)} placeholder="e.g. United Kingdom" />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-[13px] font-semibold text-[#475569] dark:text-gray-300">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-600"
                      checked={!!answers.address_under_3_years}
                      onChange={(e) => set('address_under_3_years', e.target.checked)}
                    />
                    I have lived at this address for less than 3 years
                  </label>
                </div>
              </div>

              {/* Address history is only asked for when it is actually needed,
                  which is what the tickbox above decides. */}
              {answers.address_under_3_years && (
                <div>
                  <label className={labelCls}>Previous address</label>
                  <textarea rows="3" className={`${inputCls} resize-none`} value={answers.previous_addresses} onChange={(e) => set('previous_addresses', e.target.value)} placeholder="House number, street, town or city, postcode and country" />
                </div>
              )}
            </div>
          )}

          {/* ---------- Employment details ---------- */}
          {current.id === 'emergency' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <label className={labelCls}>Contact name <span className="text-rose-500">*</span></label>
                <input
                  className={inputCls}
                  value={answers.emergency_contact_name}
                  onChange={(e) => set('emergency_contact_name', e.target.value)}
                  placeholder="Who should we call?"
                />
              </div>
              <div>
                <label className={labelCls}>Contact phone <span className="text-rose-500">*</span></label>
                <input
                  className={inputCls}
                  value={answers.emergency_contact_phone}
                  onChange={(e) => set('emergency_contact_phone', e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Relationship to you</label>
                <input
                  className={inputCls}
                  value={answers.emergency_contact_rel}
                  onChange={(e) => set('emergency_contact_rel', e.target.value)}
                  placeholder="e.g. Partner, parent, friend"
                />
              </div>

              {/* The same tickbox the Add Staff form uses. Usually the same
                  person, so the default is on and the three fields below stay
                  hidden — nobody is asked to type a name twice. */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-[13px] font-semibold text-[#475569] dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 accent-blue-600"
                    checked={!!answers.kin_same_as_emergency}
                    onChange={(e) => set('kin_same_as_emergency', e.target.checked)}
                  />
                  This emergency contact is also my next of kin
                </label>
              </div>

              {!answers.kin_same_as_emergency && (
                <div className="md:col-span-2 pt-2 border-t border-[#E2E8F0] dark:border-gray-800">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500 mt-4 mb-3">
                    Next of kin
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                    <div>
                      <label className={labelCls}>Full name</label>
                      <input
                        className={inputCls}
                        value={answers.next_of_kin?.name || ''}
                        onChange={(e) => set('next_of_kin', { ...answers.next_of_kin, name: e.target.value })}
                        placeholder="Next-of-kin name"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Relationship</label>
                      <input
                        className={inputCls}
                        value={answers.next_of_kin?.relationship || ''}
                        onChange={(e) => set('next_of_kin', { ...answers.next_of_kin, relationship: e.target.value })}
                        placeholder="e.g. Parent, sibling"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Telephone</label>
                      <input
                        className={inputCls}
                        value={answers.next_of_kin?.phone || ''}
                        onChange={(e) => set('next_of_kin', { ...answers.next_of_kin, phone: e.target.value })}
                        placeholder="Telephone"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---------- Bank details and availability ---------- */}
          {current.id === 'bank' && (
            <div className="space-y-8">
              <div>
                <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-4">
                  Used only to pay you. Check the numbers carefully — wages sent to the wrong account
                  are difficult to recover.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                  <div className="md:col-span-3">
                    <label className={labelCls}>Name on the account <span className="text-rose-500">*</span></label>
                    <input
                      className={inputCls}
                      value={answers.bank_account_name}
                      onChange={(e) => set('bank_account_name', e.target.value)}
                      placeholder="Exactly as it appears on the account"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Sort code <span className="text-rose-500">*</span></label>
                    <input
                      className={inputCls}
                      value={answers.bank_sort_code}
                      onChange={(e) => set('bank_sort_code', BANK_RULES.bank_sort_code.format(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="04-00-04"
                      aria-describedby="sort-code-hint"
                    />
                    <p id="sort-code-hint" className={bankHint('bank_sort_code')}>
                      6 digits. The hyphens are added for you.
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Account number <span className="text-rose-500">*</span></label>
                    <input
                      className={inputCls}
                      value={answers.bank_account_number}
                      onChange={(e) => set('bank_account_number', BANK_RULES.bank_account_number.format(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="8 digits"
                      aria-describedby="account-number-hint"
                    />
                    <p id="account-number-hint" className={bankHint('bank_account_number')}>
                      8 digits, no spaces.
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Confirm account number <span className="text-rose-500">*</span></label>
                    <input
                      className={inputCls}
                      value={confirmAccount}
                      onChange={(e) => setConfirmAccount(BANK_RULES.bank_account_number.format(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="Type it again"
                      aria-describedby="confirm-account-hint"
                    />
                    <p
                      id="confirm-account-hint"
                      className={`text-[12px] mt-1.5 ${accountMismatch
                        ? 'text-rose-600 dark:text-rose-400 font-semibold'
                        : 'text-[#94A3B8] dark:text-gray-500'}`}
                    >
                      {accountMismatch ? 'The two account numbers do not match.' : 'So a typo cannot slip through.'}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>
                      Building society roll number <span className="text-[#94A3B8] font-normal">(if you have one)</span>
                    </label>
                    <input
                      className={inputCls}
                      value={answers.bank_building_society_ref}
                      onChange={(e) => set('bank_building_society_ref', e.target.value)}
                      placeholder="Only for a building society account"
                    />
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* ---------- Extra documents ----------
              Only ever rendered when a manager asked for something by name.
              Each row is one request, and the form cannot be submitted until
              every one of them has a file — a manager naming a document is not
              making a suggestion. */}
          {current.id === 'documents' && (
            <div className="space-y-3">
              <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mb-2">
                PDF, Word or a photo (JPG/PNG). You can replace a file at any time before you submit.
              </p>
              {extraDocuments.map((document) => {
                const isBusy = uploadingId === document.id;
                return (
                  <div
                    key={document.id}
                    className={`flex flex-wrap items-center gap-3 p-4 rounded-xl border ${
                      document.received
                        ? 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10'
                        : 'border-[#E2E8F0] dark:border-gray-700'
                    }`}
                  >
                    <FileText className={`w-5 h-5 shrink-0 ${document.received ? 'text-emerald-600' : 'text-[#94A3B8]'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200">
                        {document.label}
                        <span className="text-rose-500 ml-1">*</span>
                      </div>
                      {document.note && (
                        <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">{document.note}</div>
                      )}
                      {document.url && (
                        <a
                          href={document.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[12.5px] text-blue-600 hover:underline break-all mt-0.5 inline-block"
                        >
                          Where to get this
                        </a>
                      )}
                      <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">
                        {document.received ? document.file_name : 'Not sent yet'}
                      </div>
                    </div>
                    {isBusy ? (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant={document.received ? 'secondary' : 'primary'} size="sm"
                          leftIcon={<Upload className="w-4 h-4" />}
                          onClick={() => pickFile(document.id)}
                        >
                          {document.received ? 'Replace' : 'Upload'}
                        </Button>
                        {document.received && (
                          <Button
                            variant="danger-soft" size="sm" icon={<X className="w-4 h-4" />}
                            aria-label={`Remove ${document.label}`}
                            onClick={() => removeUpload(document.id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ---------- Referee details ----------
              Two by default, because the organisation needs one of each kind.
              Each card says plainly what will happen to the person named on it:
              they get emailed a short form, and they see nothing else. */}
          {current.id === 'referees' && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-[#F8FAFC] dark:bg-[#2c323b] border border-[#E2E8F0] dark:border-gray-700">
                <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200">Your referees</h3>
                <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">
                  Please provide a minimum of <strong className="text-[#1E293B] dark:text-gray-200">1
                  employment reference and 1 character reference</strong>. Please contact the team if
                  you need more information.
                </p>
              </div>

              {referees.map((referee, index) => {
                const locked = referee.received || referee.status === 'Requested';
                const update = (patchRow) => setReferees((rows) => rows.map((row) => (
                  row.id === referee.id ? { ...row, ...patchRow } : row
                )));

                return (
                  <div key={referee.id} className="p-5 rounded-xl border border-[#E2E8F0] dark:border-gray-700">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h4 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200">
                        Referee {index + 1}
                      </h4>
                      {!locked && referees.length > 1 && (
                        <Button
                          variant="secondary" size="sm"
                          onClick={() => setReferees((rows) => rows.filter((row) => row.id !== referee.id))}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-4 pb-4 border-b border-[#F1F5F9] dark:border-gray-800">
                      {referee.received
                        ? 'This reference has been received. Thank you.'
                        : referee.status === 'Requested'
                          ? 'We have emailed this referee their form.'
                          : "We'll email your referee a short form to complete. They won't see the rest of your application."}
                    </p>

                    <div className="space-y-5">
                      <div>
                        <label className={labelCls}>Reference type <span className="text-rose-500">*</span></label>
                        <select
                          className={inputCls}
                          value={referee.type}
                          disabled={locked}
                          onChange={(e) => update({ type: e.target.value })}
                        >
                          {(data.reference_types || []).map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
                          {(data.reference_types || [])
                            .map((type) => `${type.label}: ${type.hint.toLowerCase()}`)
                            .join(' ')}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        <div>
                          <label className={labelCls}>Referee&apos;s full name <span className="text-rose-500">*</span></label>
                          <input
                            className={inputCls} value={referee.name} disabled={locked}
                            placeholder="Full name"
                            onChange={(e) => update({ name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Referee&apos;s email address <span className="text-rose-500">*</span></label>
                          <input
                            type="email" className={inputCls} value={referee.email} disabled={locked}
                            placeholder="name@example.com"
                            onChange={(e) => update({ email: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <label className={labelCls}>How do they know you? <span className="text-rose-500">*</span></label>
                        <input
                          className={inputCls} value={referee.relationship} disabled={locked}
                          placeholder="e.g. my previous manager or volunteering coordinator"
                          onChange={(e) => update({ relationship: e.target.value })}
                        />
                      </div>

                      {/* The only authority anyone has to email this person. */}
                      <label className="flex items-start gap-2.5 text-[13px] text-[#475569] dark:text-gray-300 pt-4 border-t border-[#F1F5F9] dark:border-gray-800 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-blue-600"
                          checked={!!referee.permission}
                          disabled={locked}
                          onChange={(e) => update({ permission: e.target.checked })}
                        />
                        I give permission for you to contact this referee about my application.
                      </label>
                    </div>
                  </div>
                );
              })}

              <Button
                variant="secondary"
                onClick={() => setReferees((rows) => [...rows, blankReferee('employment')])}
              >
                + Add another referee
              </Button>
            </div>
          )}

          {/* ---------- Review & submit ---------- */}
          {current.id === 'review' && (
            <div className="space-y-5">
              {canSubmit ? (
                <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-900/40 flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="text-[13.5px] text-[#475569] dark:text-gray-300">
                    Everything we need is here. Once you submit, the recruiting manager reviews your details — you won't be able
                    to change them afterwards, so please check them over first.
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">
                        {missing.length || missingDocs.length ? 'A few things are still needed' : 'Please check the bank details'}
                      </div>
                      <ul className="text-[13px] text-[#475569] dark:text-gray-300 mt-2 space-y-1 list-disc list-inside">
                        {missing.map((label) => <li key={label}>{label}</li>)}
                        {missingDocs.map((label) => <li key={label}>{label} (upload)</li>)}
                        {badBank.map((label) => <li key={label}>{label}</li>)}
                        {accountMismatch && <li>The two account numbers do not match.</li>}
                        {accountUnconfirmed && <li>Confirm the account number.</li>}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {[
                  ['Legal full name', fullName], ['Preferred name', answers.preferred_name],
                  ['Date of birth', answers.dob], ['NI number', answers.ni_number],
                  ['Telephone', contact.phone], ['Nationality', answers.nationality],
                  ['Address', composedAddress],
                  ['Previous address', previousAddressesToSave],
                  ['Emergency contact', [answers.emergency_contact_name, answers.emergency_contact_phone].filter(Boolean).join(' · ')],
                  ['Next of kin', answers.kin_same_as_emergency
                    ? 'Same as the emergency contact'
                    : [answers.next_of_kin?.name, answers.next_of_kin?.relationship, answers.next_of_kin?.phone]
                      .filter(Boolean).join(' · ')],
                  ['Account name', answers.bank_account_name],
                  // The account number is never echoed back in full: this page
                  // can be open on a shared screen, and the candidate only needs
                  // to recognise which account they entered.
                  ['Account', [answers.bank_sort_code, answers.bank_account_number
                    ? `••••${String(answers.bank_account_number).slice(-4)}` : ''].filter(Boolean).join(' · ')],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="text-[12px] font-semibold text-[#94A3B8] dark:text-gray-500 uppercase tracking-wide">{label}</div>
                    <div className="text-[14px] text-[#1E293B] dark:text-gray-200 mt-1 whitespace-pre-wrap break-words">{value || '—'}</div>
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>

        {/* Footer navigation.
            On a phone these stack and go full width, with the action that moves
            the form forward FIRST — three buttons sharing a 360px row squeeze
            "Submit my details" down to something nobody can hit with a thumb.
            flex-col-reverse keeps Back last on a phone and left on a desktop,
            which is where each belongs. */}
        <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <Button
            variant="secondary" leftIcon={<ChevronLeft className="w-4 h-4" />}
            disabled={step === 0} onClick={goBack} className="w-full sm:w-auto"
          >
            Back
          </Button>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2.5">
            <Button variant="ghost" loading={saving} onClick={() => persist(false)} className="w-full sm:w-auto">
              Save for later
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                variant="primary" rightIcon={<ChevronRight className="w-4 h-4" />}
                loading={saving} onClick={goNext} className="w-full sm:w-auto"
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="success" leftIcon={<Send className="w-4 h-4" />}
                loading={submitting} disabled={!canSubmit} onClick={() => persist(true)}
                className="w-full sm:w-auto"
              >
                Submit my details
              </Button>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default CandidateOnboardingForm;
